import {
  Component,
  ComponentRef,
  Injector,
  StaticProvider,
  Type,
  ViewContainerRef
} from '@angular/core';

/**
 * Dynamically injects and creates a component into the specified `ViewContainerRef` with optional parameters and providers.
 *
 * @param {ViewContainerRef} view The container where the component should be injected. Must be a valid initialized `ViewContainerRef`.
 * @param {Type<C>} component The component type to create and inject. Must be a valid Angular component class.
 * @param {any} [parameters] Optional object containing initial data or properties to be assigned to the component instance.
 * @param {StaticProvider[]} [providers] Optional array of dependency providers to extend or override the existing injector.
 * @return {ComponentRef<C>} The reference to the created component, allowing access to the instance and control over its lifecycle.
 */
export function injectComponent<C>(
  view: ViewContainerRef,
  component: Type<C>,
  parameters?: any,
  providers?: StaticProvider[],
): ComponentRef<C> {
  if (!view) {
    throw new Error(
      'ViewContainerRef is required but was not provided. Ensure the ViewContainerRef is properly initialized before calling injectComponent.'
    );
  }

  if (!component) {
    throw new Error(
      'Component type is required but was not provided. Ensure you pass a valid Angular component class.'
    );
  }

  // Create an injector, extending from the existing injector if providers are supplied
  const injector = providers
    ? Injector.create({ providers, parent: view.injector })
    : view.injector;

  // Create the component
  const cmpRef = view.createComponent<C>(component, { injector });

  // Assign initial parameters if provided
  if (parameters) {
    Object.assign(cmpRef.instance as Component, parameters);
  }

  // Trigger change detection to update the component view
  cmpRef.changeDetectorRef.detectChanges();

  // Return the created ComponentRef
  return cmpRef;
}
